import { useCallback, useEffect, useState } from 'react';
import sessionsService from '../../services/sessions';
import { Session } from '../../services/sessionModels';

type Entry = {
  session: Session | null;
  error: unknown;
  loading: boolean;
  request: Promise<Session | null> | null;
  listeners: Set<() => void>;
};

const entries = new Map<string, Entry>();

const entryFor = (id: string) => {
  let entry = entries.get(id);
  if (!entry) {
    entry = { session: null, error: null, loading: false, request: null, listeners: new Set() };
    entries.set(id, entry);
  }
  return entry;
};

const notify = (entry: Entry) => entry.listeners.forEach((listener) => listener());

export const refreshSession = (sessionId: string | number): Promise<Session | null> => {
  const entry = entryFor(String(sessionId));
  if (entry.request) return entry.request;
  entry.loading = true;
  notify(entry);
  entry.request = sessionsService
    .getSession(sessionId)
    .then((session) => {
      entry.session = session;
      entry.error = null;
      return session;
    })
    .catch((error) => {
      entry.error = error;
      return entry.session;
    })
    .finally(() => {
      entry.loading = false;
      entry.request = null;
      notify(entry);
    });
  return entry.request;
};

export const setSession = (session: Session) => {
  const entry = entryFor(String(session.id));
  entry.session = session;
  notify(entry);
};

export default function useSession(sessionId?: string | number | null) {
  const key = sessionId === undefined || sessionId === null || sessionId === '' ? null : String(sessionId);
  const [, rerender] = useState(0);

  useEffect(() => {
    if (!key) return;
    const entry = entryFor(key);
    const listener = () => rerender((n) => n + 1);
    entry.listeners.add(listener);
    if (!entry.session && !entry.request) {
      refreshSession(key);
    }
    return () => {
      entry.listeners.delete(listener);
    };
  }, [key]);

  const refetch = useCallback(() => (key ? refreshSession(key) : Promise.resolve(null)), [key]);
  const entry = key ? entries.get(key) : undefined;

  return {
    session: entry?.session ?? null,
    loading: entry ? entry.loading && !entry.session : !!key,
    error: entry?.error ?? null,
    refetch,
  };
}
